import {createAsyncThunk} from "@reduxjs/toolkit";
import {
    SerialOrOrderNumberValidationPayload,
    StepValidationPayload
} from "wolf/components/organisms/o-service-order-booking-form/o-service-order-booking-form.types";
import {RootState} from "../..";
import {AccordionArray, StepsState} from "../../../model/global";
import Api, {FinalFormPayload} from "../services/thunkApi/api";
import {specialUseCaseFields} from "../../../../mocks/utils";


export const validateStep = createAsyncThunk('serviceBookingAppSlice/validateStep', async (_, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;

    // get current step index
    const activeIndex = state.steps.activeStepIndex;
    // get step template name
    const {stepTemplate} = state.steps.steps[activeIndex]
    // get current step field data for a payload
    const stepSummary = state.steps.formSummary.steps[stepTemplate]
    // get endpoint for validation
    const endpoint: string = state.steps.steps[activeIndex].stepData.validation.url

    const mainEnquiryData = state.steps.steps.find(element => element.stepTemplate === 'MAIN_ENQUIRY')
    if (!mainEnquiryData) {
        throw new Error('Error happened while building the form');
    }
    const accordionArray: AccordionArray[] | undefined = mainEnquiryData.stepData.accordionArray;
    if (!accordionArray) {
        throw new Error('Error happened while building the form');
    }


    // init payload obj
    const payload: Record<string, any> = {};
    //if first step handle the use case if user did not add all of the products
    //and dont send them to the backend
    if (activeIndex === 0) {
        Object.values(stepSummary).forEach(e => {
            if ((e?.name.includes("2") && !accordionArray[0].added) ||
                (e?.name.includes("3") && !accordionArray[1].added) ||
                (e?.name.includes("4") && !accordionArray[2].added) ||
                (e?.name.includes("5") && !accordionArray[3].added))
                return;

            payload[e?.name as string] = e?.originalField.fieldType === 'checkbox' ? (e.checked ? e.value : null) : e?.value
        })

    } else{
        Object.values(stepSummary).forEach(e => {
            payload[e?.name as string] = e?.originalField.fieldType === 'checkbox' ? (e.checked ? e.value : null) : e?.value
        })
    }
    const stepPayload: StepValidationPayload = {
        stepTemplate,
        payload,
    }
    // call validation endpoint
    return await Api.runAsyncValidation(endpoint, stepPayload)
});

export const validateSerialOrOrderNumber = createAsyncThunk('serviceBookingAppSlice/validateSerialOrOrderNumber',
    async (_, thunkAPI) => {
        const state = thunkAPI.getState() as RootState;

        // get current step index
        const activeIndex = state.steps.activeStepIndex;
        // get step template name
        const {stepTemplate} = state.steps.steps[activeIndex]
        // get current step field data for a payload
        const stepSummary = state.steps.formSummary.steps[stepTemplate]

        if (!state.steps.steps[activeIndex].stepData.serialOrOrderNumberValidation)
            return;
        if (!state.steps.steps[activeIndex].stepData.accordionArray)
            return;


        const {
            serialOrOrderNumber, installationYear, serialOrOrderNumberTwo, installationYearTwo,
            serialOrOrderNumberThree, installationYearThree, serialOrOrderNumberFour, installationYearFour,
            serialOrOrderNumberFive, installationYearFive
        } = buildAddedProductsObject(stepSummary, state.steps.steps[activeIndex].stepData.accordionArray!);

        // get endpoint for validation
        const endpoint: string = state.steps.steps[activeIndex].stepData.serialOrOrderNumberValidation!.url
        // init payload obj
        let stepPayload: SerialOrOrderNumberValidationPayload = {};
        if (serialOrOrderNumber) {
            stepPayload = {
                ...stepPayload,
                product_1: {
                    serial_or_order_number_1: serialOrOrderNumber.value,
                    installation_year_1: installationYear.value
                }
            }
        }
        if (serialOrOrderNumberTwo) {
            stepPayload = {
                ...stepPayload,
                product_2: {
                    serial_or_order_number_2: serialOrOrderNumberTwo.value,
                    installation_year_2: installationYearTwo.value
                }
            }
        }
        if (serialOrOrderNumberThree) {
            stepPayload = {
                ...stepPayload,
                product_3: {
                    serial_or_order_number_3: serialOrOrderNumberThree.value,
                    installation_year_3: installationYearThree.value
                }
            }
        }
        if (serialOrOrderNumberFour) {
            stepPayload = {
                ...stepPayload,
                product_4: {
                    serial_or_order_number_4: serialOrOrderNumberFour.value,
                    installation_year_4: installationYearFour.value
                }
            }
        }
        if (serialOrOrderNumberFive) {
            stepPayload = {
                ...stepPayload,
                product_5: {
                    serial_or_order_number_5: serialOrOrderNumberFive.value,
                    installation_year_5: installationYearFive.value
                }
            }
        }

        /* todo if status is 408 then the 3rd API is not working */
        return await Api.runAsyncValidation(endpoint, stepPayload)
    });

const buildAddedProductsObject = (stepSummary, productFieldsState: any[]): any => {
    let fieldsToValidate = {};
    const serialOrOrderNumber = stepSummary['serial_or_order_number_1'];
    const installationYear = stepSummary['installation_year_1'];

    const serialOrOrderNumberTwo = stepSummary['serial_or_order_number_2'];
    const installationYearTwo = stepSummary['installation_year_2'];

    const serialOrOrderNumberThree = stepSummary['serial_or_order_number_3'];
    const installationYearThree = stepSummary['installation_year_3'];

    const serialOrOrderNumberFour = stepSummary['serial_or_order_number_4'];
    const installationYearFour = stepSummary['installation_year_4'];

    const serialOrOrderNumberFive = stepSummary['serial_or_order_number_5'];
    const installationYearFive = stepSummary['installation_year_5'];

    fieldsToValidate = {serialOrOrderNumber, installationYear}
    productFieldsState?.forEach(e => {
        if (e.added) {
            switch (e.panel) {
                case 1:
                    fieldsToValidate = {...fieldsToValidate, serialOrOrderNumberTwo, installationYearTwo}
                    break;
                case 2:
                    fieldsToValidate = {...fieldsToValidate, serialOrOrderNumberThree, installationYearThree}
                    break;
                case 3:
                    fieldsToValidate = {...fieldsToValidate, serialOrOrderNumberFour, installationYearFour}
                    break;
                case 4:
                    fieldsToValidate = {...fieldsToValidate, serialOrOrderNumberFive, installationYearFive}
                    break;
            }
        }
    })
    return fieldsToValidate
}


const buildFormSubmitPayload = (steps): FinalFormPayload => {
    const summary: StepsState['formSummary'] = steps.formSummary;
    const mainEnquiryData = steps.steps.find(element => element.stepTemplate === 'MAIN_ENQUIRY')
    if (!mainEnquiryData) {
        throw new Error('Error happened while building the form');
    }
    const accordionArray:AccordionArray[] = mainEnquiryData.stepData.accordionArray;

    const data: FinalFormPayload = {
        payload: {},
        formData: new FormData()
    };


    for (const [key, elements] of Object.entries(summary.steps)) {
        data.payload[key] = {};

        for (const [fieldKey, field] of Object.entries(elements)) {
            const fieldType = field?.originalField.fieldType;
            if (key) {
                if (key === 'MAIN_ENQUIRY'){
                    const isNameInSpecialUseCaseArray = specialUseCaseFields.includes(fieldKey);
                    if (isNameInSpecialUseCaseArray){
                        if ((fieldKey.includes("2") && !accordionArray[0].added) ||
                            (fieldKey.includes("3") && !accordionArray[1].added) ||
                            (fieldKey.includes("4") && !accordionArray[2].added) ||
                            (fieldKey.includes("5") && !accordionArray[3].added))
                            continue;
                        else if((fieldKey.includes("2") && accordionArray[0].added) ||
                            (fieldKey.includes("3") && accordionArray[1].added) ||
                            (fieldKey.includes("4") && accordionArray[2].added) ||
                            (fieldKey.includes("5") && accordionArray[3].added)){
                            data.payload[key][fieldKey] = {
                                name: field!.name,
                                value: field!.value,
                                fieldType: fieldType!,
                            }
                        }
                    } else
                        data.payload[key][fieldKey] = {
                            name: field!.name,
                            value: field!.value,
                            fieldType: fieldType!,
                        }
                } else{
                    data.payload[key][fieldKey] = {
                        name: field!.name,
                        value: field!.value,
                        fieldType: fieldType!,
                    }
                }
                let checked = false;
                if (fieldType === 'checkbox') {
                    checked = !!field?.checked
                    data.payload[key][fieldKey]['checked'] = checked
                }

                // add to form data
                let formDataValue = data.payload[key][fieldKey].value;
                if (fieldType === 'checkbox' && !checked) {
                    // support for checkboxes/values
                    formDataValue = '';
                }
                data.formData.append(fieldKey, formDataValue);

            }
        }
    }
    return data;
}

export const submitForm = createAsyncThunk('serviceBookingAppSlice/submitForm', async (_, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;

    // get submit URL
    const endpoint: string = state.steps.formSubmitUrl;
    if (!endpoint) throw new Error('Missing form submit URL!')


    // build payload based on Redux data
    const payload = buildFormSubmitPayload(state.steps);

    return await Api.submitFinalForm(endpoint, payload)
});

